import { NgRedux } from '@angular-redux/store';
import {
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
} from 'ag-grid-community';
import { IFilterAngularComp } from 'ag-grid-angular';
import {
  castArray,
  chain,
  compact,
  concat,
  difference,
  every,
  filter,
  find,
  get,
  head,
  intersection,
  isEmpty,
  isEqual,
  isNil,
  lt,
  map,
  set,
  some,
  uniq,
} from 'lodash';
import {
  BehaviorSubject,
  isObservable,
  Observable,
  observableCombineLatest,
  observableFrom,
  observableOf,
  ReplaySubject,
  rx,
  Subscription,
} from '@shared/rx';

import {
  ColumnFilterModel,
  ColumnFilterParams,
  ComponentStaticText,
  FilterItemsSourceEnum,
  FilterOptionType,
  IFilterQuery,
  InitFilterParams,
  Primitive,
  SelectOption,
  TrackByValue,
} from '@shared/types';
import { IAppState } from '@store/reducers';
import { APIActions } from '@store/actions/api-actions';
import { defaultOptionMapper, trackByValue } from '@shared/utils/common-utils';
import {
  DEFAULT_DOCUMENT_OPERATION_OPTIONS,
  ENTITY_ID_PATH,
} from '@config/global-constants';
import { XprtDataService } from '@shared/services';
import { getFilterOptionsFromFilterParams } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { stringCaseInsensitiveComparator } from '@app/shared/common';

type MixedFilterParams = ColumnFilterParams &
  InitFilterParams & {
    filterComparator?: (
      firstOption: SelectOption,
      secondOption: SelectOption,
    ) => number;
  };

/** Adds a prefix for the 'event' argument in the {@link LogService} methods */

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'entity-filter-cell',
  templateUrl: 'entity-filter.component.html',
  styleUrls: ['entity-filter.component.scss'],
})
export class XVIEntityGridFilterComponent
  implements IFilterAngularComp, OnDestroy
{
  @ViewChildren('itemCheckbox') public checkboxes: QueryList<ElementRef>;
  @ViewChild('filterInput', { static: false }) public filterInput: ElementRef;
  @ViewChild('selectAllCheckbox', { static: false })
  public selectAllCheckbox: ElementRef;

  public filterActive: boolean = false;
  public isPartialSelect: boolean = false;
  public typeControl: FormControl = new FormControl('');
  public filterControl: FormControl = new FormControl('');
  public filterOptions: SelectOption[];
  public items: SelectOption[] = [];
  public selectedItems: Primitive[] = [];
  public noVisibleItems: boolean = false;
  public suppressSelectAllControl: boolean = false;
  public suppressSearchControl: boolean = false;
  public item$: Observable<SelectOption[]>;
  public subscriptions: Subscription[] = [];
  public entityType: string;
  public column: any;
  public isEqualMode: boolean = false;
  public itemSourceToIdKeyPathMap: { [key in FilterItemsSourceEnum]?: string } =
    {
      [FilterItemsSourceEnum.XPRT_USERS]: 'id',
      [FilterItemsSourceEnum.XPRT_EVENTS]: 'id',
      [FilterItemsSourceEnum.XPRT_SITES]: 'id',
    };
  public staticTextContent: ComponentStaticText = {
    noItems: 'No Items To Display',
    selectAll: 'Select All',
  };
  public trackByValue: TrackByValue = trackByValue;
  private params: MixedFilterParams;
  private modelSelectionSubject: ReplaySubject<Primitive[] | Primitive> =
    new ReplaySubject();
  private columnVisibility$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  private suppressUIFiltering: boolean;
  private filterItemsSource: FilterItemsSourceEnum;
  private filterItemSourceQuery: IFilterQuery = {};
  private filterItemSource$: Observable<any>;

  private hidePopup?: () => void;
  private omitField: string[] = [];

  public constructor(
    public ngRedux: NgRedux<IAppState>,
    public apiActions: APIActions,
    public xprtDataService: XprtDataService,
  ) {}

  public agInit(params: InitFilterParams): void {
    /*
      need to set params as 'any' because of tslint errors
      while using 'XENIAL-UI-SHARED' as a local package
    */
    this.params = params as any;
    this.staticTextContent = {
      ...this.staticTextContent,
      ...get(params, 'i18n', {}),
    };
    this.entityType = this.params.entity;
    this.column = this.params.column;
    this.initVisibilityChangeHandler(params);
    this.suppressSelectAllControl = get(
      params,
      'suppressSelectAllControl',
      false,
    );
    this.suppressSearchControl = get(params, 'suppressSearchControl', false);
    this.suppressUIFiltering = get(params, 'suppressUIFiltering', false);
    this.filterItemsSource = get(params, 'filterItemsSource', null);
    this.filterItemSourceQuery = get(
      params,
      'filterItemSourceQuery',
      this.filterItemSourceQuery,
    );

    const itemSourceGetter: Function = this.getItemSourceGetter(
      this.filterItemsSource,
    );
    const itemSourceIdKeyPath: string =
      this.itemSourceToIdKeyPathMap[this.filterItemsSource] || ENTITY_ID_PATH;

    const filterOptionsMapper = get(params, 'filterOptionsMapper');
    const filterOptions = getFilterOptionsFromFilterParams(params);

    this.filterOptions = filterOptionsMapper
      ? filterOptionsMapper(filterOptions)
      : filterOptions;

    if (!isNil(get(params, 'values'))) {
      if (isObservable(get(params, 'values'))) {
        this.filterItemSource$ = get(
          params,
          'values',
        ) as unknown as Observable<any>;
      } else {
        this.filterItemSource$ = observableOf(get(params, 'values'));
      }
    } else {
      this.filterItemSource$ = observableFrom(itemSourceGetter()).pipe(
        rx.map((response: any) => response.items || response),
        rx.map((items: any[]) => filter(items, this.params.entitySelector)),
        rx.map((items: any[]) =>
          defaultOptionMapper(items, itemSourceIdKeyPath),
        ),
      );
    }

    this.item$ = observableCombineLatest([
      this.filterItemSource$,
      this.filterControl.valueChanges.pipe(
        rx.startWith(''),
        rx.map((text) => this.createTextMatcher(text)),
      ),
      this.modelSelectionSubject.asObservable().pipe(rx.startWith([])),
    ]).pipe(
      rx.debounceTime(0),
      rx.map(([items, filterControl, modelSelection]) =>
        this.sortItems(
          items.map((item) => ({
            ...item,
            checked: this.selectedItems.includes(item.value),
            hidden: !filterControl.test(item.text),
          })),
          modelSelection,
        ),
      ),
      rx.map(compact),
    );

    this.subscriptions.push(
      this.item$.subscribe((items) => {
        this.items = items;
        this.noVisibleItems = items.every(({ hidden }) => hidden);

        params.api.refreshHeader();
      }),
    );

    this.subscriptions.push(
      this.item$
        .pipe(
          rx.map((items) =>
            items
              .filter((item) => item.checked)
              .map((item) => item.text)
              .join(', '),
          ),
          rx.scan((acc, data) => ({ count: acc.count + 1, data }), {
            count: 0,
            data: '',
          }),
          // during filter initialization this method fire as we initially retrieve items
          // when component initialized filter change event should not happen
          rx.filter(
            ({ count }) =>
              isEqual(this.params.api?.getModel()?.getType(), 'clientSide') ||
              lt(count, 1),
          ),
          rx.map(({ data }) => data),
          rx.distinctUntilChanged(),
        )
        .subscribe(() => {
          // recalculate filter active state
          if (!this.filterActive) {
            this.filterActive = !isEmpty(this.selectedItems);
          }

          this.params.filterChangedCallback();
        }),
    );

    this.resetData();
    this.filterTypeListener();
  }

  public afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup;
  }

  public filterTypeListener(): void {
    this.subscriptions.push(
      this.typeControl.valueChanges.subscribe((type: string) => {
        this.isEqualMode = false;

        if (type === 'isEqual') {
          this.isEqualMode = true;

          if (this.getCheckboxSelectionData().length > 1) {
            this.checkboxes
              .filter((checkbox) => checkbox.nativeElement.checked)
              .forEach(({ nativeElement }) => (nativeElement.checked = false));
          }
        }
      }),
    );
  }

  public selectAllClick(): void {
    const selectAllState: boolean =
      this.selectAllCheckbox.nativeElement.checked;
    const checkedVisibleItems = chain(this.items)
      .filter((val) => !val.hidden)
      .map((val) => val.value)
      .value();

    this.isPartialSelect = false;
    this.checkboxes.forEach(({ nativeElement }) => {
      nativeElement.checked = !!selectAllState;
    });
    this.items = map(this.items, (item) => ({
      ...item,
      checked: !!selectAllState,
    }));

    if (selectAllState) {
      this.selectedItems = uniq([
        ...this.selectedItems,
        ...checkedVisibleItems,
      ]);
    } else {
      this.selectedItems = filter(
        this.selectedItems,
        (val) => !checkedVisibleItems.includes(val),
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public isFilterActive(): boolean {
    return this.filterActive;
  }

  public doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (this.suppressUIFiltering) return true;

    const valueGetter = this.params?.colDef?.filterParams?.filterValueGetter;
    let cellValue: Primitive[] | any;
    if (valueGetter) {
      cellValue = valueGetter(params.data, { node: params.node });
    } else {
      /** Support deep fields divided via dot notation */
      const path = get(this, 'params.colDef.path');
      cellValue = castArray(get(params.data, path));
    }

    const type: FilterOptionType = this.typeControl.value;

    switch (type) {
      case 'containsAll':
        return isEmpty(difference(this.selectedItems, cellValue));
      case 'containsAny':
        return some(intersection(this.selectedItems, cellValue));
      case 'notContains':
        return isEmpty(intersection(this.selectedItems, cellValue));
      default:
        return false;
    }
  }

  // method returns filter model on agGrid demand
  public getModel(): ColumnFilterModel & { omitField?: string[] } {
    const selection: SelectOption[] = this.getCheckboxSelectionData();
    this.selectedItems = concat(
      this.selectedItems,
      map(selection, 'value').filter(
        (value) => !this.selectedItems.includes(value),
      ),
    );
    const headerText: string[] = !isEmpty(this.items)
      ? map(
          this.selectedItems.map((value) => find(this.items, { value })),
          'text',
        )
      : [];

    this.refreshSelectAllState();

    const columnFilterModel = {
      headerText,
      type: this.typeControl.value,
      filter: this.selectedItems,
    };

    if (!isEmpty(this.omitField)) {
      set(columnFilterModel, 'omitField', this.omitField);
    }

    return columnFilterModel;
  }

  public setModel(model: ColumnFilterModel): void {
    this.omitField = get(model, 'omitField', []);
    const filterType: FilterOptionType = get(model, 'type');
    this.setData(filterType ? model : this.params.default);
    this.modelSelectionSubject.next(get(model, 'filter', []));
  }

  // clear button click
  public resetData(): void {
    this.setData(this.params.default);
    // fix needed to set correct state
    setTimeout(() => {
      if (this.filterInput) {
        this.setFocusToFilterInput();
      }
      this.refreshSelectAllState();
    }, 0);
  }

  public resetBtnClick(): void {
    this.resetData();
    this.hidePopup?.();
    this.applySelection();
  }

  public applyClick(): void {
    this.applySelection();
    this.hidePopup?.();
  }

  public applySelection(): void {
    const selection: SelectOption[] = this.getCheckboxSelectionData();
    this.selectedItems = map(selection, 'value');
    this.filterActive = !isEmpty(this.selectedItems);
    this.params.filterChangedCallback();
  }

  public checkboxClicked(clickedCheckboxIndex: number): void {
    this.isEqualModeHandler(clickedCheckboxIndex);
    this.refreshSelectAllState();
  }

  public isEqualModeHandler(clickedCheckboxIndex: number): void {
    if (this.isEqualMode) {
      this.checkboxes
        .filter((checkbox, index) => index !== clickedCheckboxIndex)
        .forEach(({ nativeElement }) => (nativeElement.checked = false));
    }
  }

  public refreshSelectAllState(): void {
    if (this.suppressSelectAllControl || this.noVisibleItems) {
      return;
    }

    const checkboxesData: SelectOption[] = this.getCheckboxSelectionData();
    let isAllChecked: boolean;
    if (checkboxesData.length !== 0) {
      isAllChecked = checkboxesData.length === this.items.length;
    }
    this.isPartialSelect = false;

    if (
      !isNil(this.selectAllCheckbox) &&
      !isNil(this.selectAllCheckbox.nativeElement)
    ) {
      this.selectAllCheckbox.nativeElement.checked = isAllChecked;
    }

    if (!isEmpty(checkboxesData) && checkboxesData.length < this.items.length) {
      this.isPartialSelect = true;
    }
  }

  private onColumnVisible = (): void => {
    this.columnVisibility$.next(this.column.visible);
  };

  private initVisibilityChangeHandler(params: InitFilterParams): void {
    /**
     * reference entities should be fully refetched on selected store change,
     * This subscription fetches references on store change and on selected columns change
     */
    this.subscriptions.push(
      observableCombineLatest([this.columnVisibility$])
        .pipe(
          rx.distinctUntilChanged(isEqual),
          rx.map(head),
          rx.filter(Boolean),
        )
        .subscribe(() =>
          this.apiActions.referenceDataRequired(
            [this.entityType],
            this.entityType,
          ),
        ),
    );

    /** initial data request - before register eventListener to handle column visibility */
    this.onColumnVisible();

    (params as any).api.addEventListener('columnVisible', this.onColumnVisible);
  }

  private setData(model: ColumnFilterModel & { source?: string }): void {
    this.selectedItems = castArray(model?.filter ?? []);
    if (this.checkboxes && isEmpty(this.selectedItems)) {
      this.checkboxes.forEach(
        ({ nativeElement }) => (nativeElement.checked = false),
      );
    }

    const defaultType: FilterOptionType = head(this.params.filterOptions);
    this.typeControl.setValue(model?.type ?? defaultType);
    this.filterControl.setValue('');
  }

  private getCheckboxSelectionData(): SelectOption[] {
    let checked: SelectOption[] = [];
    let useDataCheck = true;
    this.checkboxes.forEach(({ nativeElement }) => {
      useDataCheck = false;
      if (nativeElement.checked) {
        checked.push(find(this.items, { value: nativeElement.value } as any));
      }
    });

    if (
      isEmpty(checked) &&
      useDataCheck &&
      every(this.items, (item) => get(item, 'checked'))
    ) {
      checked = filter(this.items, (item) => get(item, 'checked'));
    }

    return filter(checked, (checkbox) => !checkbox.hidden);
  }

  private setFocusToFilterInput(): void {
    (this.filterInput.nativeElement as HTMLElement).focus();
  }

  private sortItems(items: any[], modelSelection): SelectOption[] {
    const topItemValues: string[] = modelSelection.filter((value) =>
      this.selectedItems.includes(value),
    );
    const topItems: SelectOption[] = topItemValues.map((value) =>
      find(items, { value } as any),
    );

    const rest: SelectOption[] = filter(
      items,
      ({ value }) => !topItemValues.includes(value),
    );

    const comparator =
      this.params?.filterComparator || this.defaultComparator.bind(this);

    return topItems.concat(rest.sort(comparator));
  }

  /**
   * Default comparator for sorting filter items alphabetically (case-insensitive).
   *
   * @param currentOption - The first option to compare
   * @param compareOption - The second option to compare
   * @returns sorted options order as a number (-1, 0, 1)
   */
  private defaultComparator(
    currentOption: SelectOption,
    compareOption: SelectOption,
  ): number {
    const currentText = currentOption.text;
    const compareText = compareOption.text;

    return stringCaseInsensitiveComparator(currentText, compareText);
  }

  private getItemSourceGetter(
    source: FilterItemsSourceEnum,
  ): () => Promise<any> | Observable<any> {
    if (source === FilterItemsSourceEnum.XPRT_EVENTS) {
      return this.xprtDataService.getEventsByFilter.bind(
        this.xprtDataService,
        this.filterItemSourceQuery,
      );
    }

    if (source === FilterItemsSourceEnum.XPRT_SITES) {
      return this.xprtDataService.getAllSitesByFilter.bind(
        this.xprtDataService,
        this.filterItemSourceQuery,
      );
    }

    return this.apiActions.fetchList.bind(
      this.apiActions,
      this.entityType,
      this.filterItemSourceQuery,
      DEFAULT_DOCUMENT_OPERATION_OPTIONS,
    );
  }

  /**
   * Creates a simple text matcher for filtering
   *
   * @param text User input text
   * @returns Object with test method for string matching
   */
  private createTextMatcher(text: string): {
    test: (input: string) => boolean;
  } {
    if (!text) {
      return { test: () => true };
    }

    const searchText = text.toLowerCase();

    return {
      test: (input: string) => input.toLowerCase().includes(searchText),
    };
  }
}
